import { computed, inject } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { HrProUpsellDialogService } from '@app/services/hr-pro-upsell/hr-pro-upsell-dialog.service';
import { useHrProOrUpsellVisible } from '@app/services/hr-pro-upsell/use-hr-pro-or-upsell-visible';
import { Store } from '@ngrx/store';
import { selectAccountHasHrPlus } from '@reducers/account/account.selector';
import type { AppState } from '@reducers/interfaces';
import { getLocationEntities } from '@reducers/orm/location/location.service';
import type { Observable } from 'rxjs';

import type { DecisionListState } from './decision-list-widget.component';
import { scopeLocationName } from './scope-location';

interface DecisionListResponse {
  scopeLocationIds: string[] | null;
}

interface DecisionListOptions<TResponse extends DecisionListResponse, TRow> {
  load: () => Observable<TResponse>;
  toRows: (response: TResponse) => TRow[];
}

export const useDecisionList = <TResponse extends DecisionListResponse, TRow>({
  load,
  toRows,
}: DecisionListOptions<TResponse, TRow>) => {
  const store = inject<Store<AppState>>(Store);
  const hrProDialog = inject(HrProUpsellDialogService);

  const locations = store.selectSignal(getLocationEntities);
  const hasHrPro = store.selectSignal(selectAccountHasHrPlus);

  const resource = rxResource({
    params: () => (hasHrPro() ? {} : undefined),
    stream: load,
  });

  const rows = computed(() => (resource.hasValue() ? toRows(resource.value()) : []));

  const state = computed((): DecisionListState => {
    if (!hasHrPro()) {
      return 'locked';
    }
    if (resource.error()) {
      return 'error';
    }
    if (resource.isLoading() || !resource.hasValue()) {
      return 'loading';
    }

    return rows().length ? 'ready' : 'clear';
  });

  return {
    isHrProOrUpsellVisible: useHrProOrUpsellVisible(),
    state,
    rows,
    count: computed(() => rows().length),
    scopeLocation: computed(() =>
      resource.hasValue() ? scopeLocationName(resource.value().scopeLocationIds, locations()) : null,
    ),
    reload: () => resource.reload(),
    openUpsell: () => hrProDialog.open(),
  };
};
